// UserModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Button, Card, Badge, ListGroup, Alert } from 'react-bootstrap';
import { User, Group } from '../../../types/knowledge-base';
import { Mail } from 'lucide-react';
import { buildIsConsumerUrl } from '../../../config/api.config';

interface UserFormData {
    email: string;
    fullName: string;
    groupIds: string[];
    status: 'active' | 'inactive';
}

interface UserModalProps {
    isOpen: boolean;
    onClose: () => void;
    formData: UserFormData;
    setFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
    selectedUser: User | null;
    groups: Group[];
    onSubmit: () => Promise<void>;
    isLoading: boolean;
    organizationId: string;
    organizationDomain: string;
    emailError?: string;
}

const UserModal: React.FC<UserModalProps> = ({
                                                 isOpen,
                                                 onClose,
                                                 formData,
                                                 setFormData,
                                                 selectedUser,
                                                 groups,
                                                 onSubmit,
                                                 isLoading,
                                                 organizationId,
                                                 organizationDomain,
                                                 emailError,
                                             }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isConsumerOrg, setIsConsumerOrg] = useState(false);
    const isProcessingGroupSelection = useRef(false);

    useEffect(() => {
        const checkConsumerOrg = async () => {
            try {
                const response = await fetch(buildIsConsumerUrl(organizationId));
                if (!response.ok) {
                    throw new Error('Failed to check organization type');
                }
                const data = await response.json();
                setIsConsumerOrg(data.isConsumer);
            } catch (error) {
                console.error('Error checking consumer organization:', error);
                setIsConsumerOrg(false);
            }
        };

        checkConsumerOrg();
    }, [organizationId]);

    const filteredGroups = groups
        .filter(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    const handleModalClose = () => {
        if (isProcessingGroupSelection.current) {
            return;
        }
        onClose();
    };

    return (
        <Modal show={isOpen} onHide={handleModalClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {selectedUser ? 'Edit User' : 'Invite User'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.fullName}
                            onChange={(e) => setFormData(prev => ({...prev, fullName: e.target.value}))}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                            required
                            isInvalid={!!emailError}
                        />
                        {emailError ? (
                            <Form.Control.Feedback type="invalid">
                                {emailError}
                            </Form.Control.Feedback>
                        ) : (
                            <Form.Text className="text-muted">
                                {!isConsumerOrg ? `Email must be from the ${organizationDomain} domain` : 'Enter any valid email address'}
                            </Form.Text>
                        )}
                    </Form.Group>

                    {!selectedUser && (
                        <Alert variant="info" className="mb-3">
                            <Mail size={18} className="me-2" />
                            An invitation email will be sent to the user with instructions to set up their account.
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Groups</Form.Label>
                        <Card>
                            <Card.Body>
                                <Form.Control
                                    type="text"
                                    placeholder="Search groups..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="mb-3"
                                />

                                {formData.groupIds.length > 0 && (
                                    <div className="mb-3 pb-2 border-bottom">
                                        {formData.groupIds
                                            .map(groupId => groups.find(g => g.id === groupId))
                                            .filter(Boolean)
                                            .map(group => (
                                                <Badge
                                                    key={group!.id}
                                                    bg="secondary"
                                                    className="me-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={e => e.preventDefault()}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        isProcessingGroupSelection.current = true;

                                                        setTimeout(() => {
                                                            const isSelected = formData.groupIds.includes(group!.id);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                groupIds: isSelected
                                                                    ? prev.groupIds.filter(id => id !== group!.id)
                                                                    : [...prev.groupIds, group!.id]
                                                            }));

                                                            isProcessingGroupSelection.current = false;
                                                        }, 10);
                                                    }}
                                                >
                                                    {group!.name} ×
                                                </Badge>
                                            ))}
                                    </div>
                                )}

                                <ListGroup style={{ maxHeight: '200px', overflow: 'auto' }}>
                                    {filteredGroups.map(group => (
                                        <ListGroup.Item
                                            key={group.id}
                                            action
                                            active={formData.groupIds.includes(group.id)}
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();

                                                isProcessingGroupSelection.current = true;

                                                setTimeout(() => {
                                                    const isSelected = formData.groupIds.includes(group.id);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        groupIds: isSelected
                                                            ? prev.groupIds.filter(id => id !== group.id)
                                                            : [...prev.groupIds, group.id]
                                                    }));

                                                    isProcessingGroupSelection.current = false;
                                                }, 10);
                                            }}
                                        >
                                            {group.name}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                status: e.target.value as 'active' | 'inactive'
                            }))}
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={onSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Loading...
                        </>
                    ) : (
                        selectedUser ? 'Update User' : 'Invite User'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserModal;